// Phase 40 Plan 03 Task 2 — SyncStatusIndicator pill (UI-SPEC §11).
//
// 4-state pill: synced (Settings only) | syncing N | failed N | offline.
// Tappable in 'failed' state — opens DeadLetterModal via the parent's onTap.
//
// Props:
//   status:  'synced' | 'syncing' | 'failed' | 'offline'
//   count:   number (changes-pending in syncing, dead-letter count in failed)
//   onTap:   () => void  (only meaningful in 'failed' state)

import React from 'react';
import { CheckCircle2, Loader2, AlertTriangle, WifiOff } from 'lucide-react';

const STATE_STYLES = {
  synced:  { bg: 'var(--c-tint)',          fg: 'var(--c-success)',       icon: CheckCircle2,  label: (n) => 'Synced' },
  syncing: { bg: 'var(--c-blight)',        fg: 'var(--c-text-2)',        icon: Loader2,       label: (n) => `Syncing ${n} change${n === 1 ? '' : 's'}` },
  failed:  { bg: 'var(--c-overdue-bg)',    fg: 'var(--c-overdue-text)',  icon: AlertTriangle, label: (n) => `${n} change${n === 1 ? '' : 's'} failed to sync` },
  offline: { bg: 'var(--c-upcoming-bg)',   fg: 'var(--c-upcoming-text)', icon: WifiOff,       label: (n) => 'Offline — changes saved locally' },
};

export default function SyncStatusIndicator({ status = 'synced', count = 0, onTap }) {
  const style = STATE_STYLES[status] || STATE_STYLES.synced;
  const Icon = style.icon;
  const isInteractive = status === 'failed' && typeof onTap === 'function';
  // prefers-reduced-motion: degrade spinner to static icon by guarding the
  // animation prop. Browser CSS animation is honoured when motion preference
  // is allowed via Tend's existing motion machinery.
  const spinnerAnim = status === 'syncing' ? 'tendSpin 1s linear infinite' : 'none';

  const Pill = (
    <span
      role="status"
      aria-live="polite"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: style.bg, color: style.fg,
        borderRadius: 999, padding: '6px 12px',
        fontSize: '0.8125rem', fontWeight: 600,
        cursor: isInteractive ? 'pointer' : 'default',
        border: 'none', fontFamily: 'inherit',
      }}
    >
      <Icon
        size={16}
        style={{ flexShrink: 0, animation: spinnerAnim }}
        aria-hidden="true"
      />
      <small style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{style.label(count)}</small>
    </span>
  );

  if (isInteractive) {
    return (
      <button
        type="button"
        onClick={onTap}
        aria-label={style.label(count)}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          font: 'inherit',
        }}
      >
        {Pill}
      </button>
    );
  }
  return Pill;
}
